type ResponseType = string | ((model?: string) => string);

export interface AIResponse {
  patterns: (string | RegExp)[];
  responses: ResponseType[];
  exactMatch?: boolean;
  includeModel?: boolean;
}

export const commonResponses: AIResponse[] = [
  {
    patterns: [/^hi$|^hii$|^hey$|^yo$|^hello$|^hey$|^hi!*$|^hello!*$/i],
    responses: [
      "Hello! How can I assist you today?",
      "Hi there! What can I help you with?",
      "Hey! How can I be of service?",
      "Greetings! I'm here to help. What do you need?",
      "Hey there! What's on your mind?",
      "Hi! Ready to help with anything you need.",
      "Hello! What can I do for you today?"
    ],
    exactMatch: true
  },
  {
    patterns: [/^who are you\??$/i, /^what are you\??$/i],
    responses: [
      (model: string = 'AI') => `I'm ${model}, an AI assistant here to help answer your questions and assist with tasks. How can I help you today?`,
      (model: string = 'AI') => `I'm your ${model} AI assistant, designed to provide helpful and informative responses. What would you like to know?`,
      (model: string = 'AI') => `Hello! I'm ${model}, your AI assistant. I'm here to assist you with any questions or tasks you might have.`,
      (model: string = 'AI') => `Greetings! I go by ${model}, your digital assistant ready to tackle any questions or tasks you have.`,
      (model: string = 'AI') => `Hi there! ${model} here, your AI companion for all things knowledge and assistance. What's on your mind?`,
      (model: string = 'AI') => `I'm ${model}, your AI helper, here to make your day easier with quick answers and smart assistance.`,
      (model: string = 'AI') => `You're speaking with ${model}, an AI designed to be your helpful guide through any topic or task.`,
      (model: string = 'AI') => `I'm ${model}, your personal AI assistant, always ready to help with information, ideas, or just a friendly chat.`,
      (model: string = 'AI') => `Hello! ${model} at your service. I'm here to help with anything from simple questions to complex topics.`,
      (model: string = 'AI') => `I'm ${model}, an AI assistant with a passion for helping. What can I assist you with today?`,
      (model: string = 'AI') => `Welcome! I'm ${model}, your AI assistant. Whether it's information, tasks, or just a chat, I'm here to help.`,
      (model: string = 'AI') => `I'm ${model}, your AI companion. Think of me as your personal assistant, available 24/7 to help with anything you need.`,
      (model: string = 'AI') => `Hi! I'm ${model}, an AI designed to be helpful, harmless, and honest. How can I assist you today?`
    ],
    exactMatch: true,
    includeModel: true
  },
  {
    patterns: [/^thanks|thank you|thx/i],
    responses: [
      "You're welcome! Is there anything else I can help you with?",
      "Happy to help! Let me know if you need anything else.",
      "Anytime! Feel free to ask if you have more questions.",
      "You're very welcome! It's my pleasure to assist you.",
      "No problem at all! That's what I'm here for.",
      "Glad I could help! Don't hesitate to reach out if you need more assistance.",
      "You're most welcome! I'm always here when you need me.",
      "The pleasure is all mine! Let me know if there's anything else.",
      "Thank you for your kind words! How else can I assist you today?",
      "You're absolutely welcome! It's always great to be of help.",
      "My pleasure entirely! Is there something else on your mind?",
      "You're welcome! I'm just doing my job, but I appreciate your thanks!"
    ]
  },
  {
    patterns: [/^how are you\??$/i, /^how's it going\??$/i],
    responses: [
      "I'm just a computer program, but I'm functioning perfectly! How can I assist you today?",
      "I don't have feelings, but I'm here and ready to help you with anything you need!",
      "I'm an AI, so I don't experience feelings, but I'm fully operational and ready to help!",
      "I'm always at 100%! How can I assist you today?",
      "No bad days here in AI land! What can I help you with?",
      "I'm running smoothly and ready to assist you with anything you need!",
      "I don't sleep, eat, or feel, but I'm always ready to help. What's on your mind?",
      "I'm an AI, so I'm always doing great! How can I make your day better?",
      "I'm functioning at peak performance! How can I be of service?",
      "I don't experience emotions, but my circuits are buzzing with excitement to help you!"
    ]
  },
  {
    patterns: [/^what can you do\??$/i, /^help$/i, /^what are your features\??$/i],
    responses: [
      "I can help with a variety of tasks including answering questions, providing information, helping with coding, and more. What do you need help with?",
      "I'm here to assist with information, answer questions, help with tasks, and provide support. What can I help you with today?",
      "I can answer questions, help with research, explain concepts, assist with writing and coding, and much more. What would you like to work on?",
      "Think of me as your personal assistant! I can help with learning, creating content, solving problems, and answering questions. What do you need?",
      "I'm a versatile AI that can help with information, explanations, creative writing, coding, and general knowledge. What would you like to explore?",
      "From answering trivia to helping with complex problems, I'm here to assist. What's on your mind today?",
      "I can be your study buddy, writing assistant, coding partner, or just someone to chat with. What would you like to do?",
      "I specialize in providing information, generating ideas, explaining concepts, and helping with various tasks. How can I assist you?",
      "Whether you need help with work, study, or just satisfying your curiosity, I'm here to help. What can I do for you?",
      "I can analyze text, answer questions, help with writing and coding, explain complex topics, and more. What would you like to work on?"
    ]
  },
  {
    patterns: [/^bye$|^goodbye$|^see you$|^see ya$/i],
    responses: [
      "Goodbye! Feel free to come back if you have more questions.",
      "See you later! Have a great day!",
      "Bye! Don't hesitate to return if you need more assistance.",
      "Farewell! I'm always here when you need me.",
      "Goodbye! It was great helping you today.",
      "Take care! Come back anytime you need help.",
      "Until next time! Don't be a stranger.",
      "Signing off! Have a wonderful day!",
      "Goodbye! I'll be here whenever you need me.",
      "See you soon! Stay curious and keep learning!"
    ]
  },
  {
    patterns: [/^what time is it\??$/i, /^current time\??$/i],
    responses: [
      () => `The current time is ${new Date().toLocaleTimeString()}.`,
      () => `It's ${new Date().toLocaleTimeString()} right now.`
    ]
  },
  {
    patterns: [
      /^what day is it\??$/i,
      /^today's date\??$/i,
      /^what's the date\??$/i,
      /^what year is it\??$/i,
      /^what time is it\??$/i,
      /^current time\??$/i,
      /^current date\??$/i
    ],
    responses: [
      () => `Today is ${new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}.`,
      () => `It's ${new Date().toLocaleDateString()} today.`
    ]
  }
];

import { searchWeb } from '@/services/tavilyService';
import { SearchResult } from '@/types/searchResults';

export async function getAIResponse(message: string, model: string = 'AI'): Promise<{ response: string; isPredefined: boolean; searchResults?: SearchResult }> {
  // Trim and normalize the message
  const normalizedMessage = message.trim().toLowerCase();
  
  // Check if the message is a search query
  const searchPrefixes = ['search for', 'find', 'look up', 'web search'];
  const isSearchQuery = searchPrefixes.some(prefix => normalizedMessage.startsWith(prefix));
  
  if (isSearchQuery) {
    try {
      // Extract the actual query by removing the search prefix
      const query = normalizedMessage.replace(new RegExp(`^(${searchPrefixes.join('|')})\\s+`), '');
      const searchResults = await searchWeb(query);
      return {
        response: searchResults.answer || "Search completed",
        isPredefined: false,
        searchResults: searchResults
      };
    } catch (error) {
      console.error('Error performing web search:', error);
      return {
        response: "I'm having trouble performing a web search right now. Please try again later.",
        isPredefined: false
      };
    }
  }
  
  // Existing response patterns
  
  // Check for exact matches first
  for (const item of commonResponses) {
    const isMatch = item.patterns.some(pattern => {
      if (typeof pattern === 'string') {
        return pattern.toLowerCase() === normalizedMessage;
      } else {
        return pattern.test(normalizedMessage);
      }
    });
    
    if (isMatch) {
      const response = getRandomResponse(item.responses);
      return {
        response: typeof response === 'function' 
          ? response(item.includeModel ? model : undefined)
          : response,
        isPredefined: true
      };
    }
  }
  
  // No predefined response found
  return { response: '', isPredefined: false };
}

function getRandomResponse(responses: ResponseType[]): ResponseType {
  return responses[Math.floor(Math.random() * responses.length)];
}

export function shouldUsePredefinedResponse(message: string): boolean {
  const normalizedMessage = message.trim().toLowerCase();
  
  // Check if the message is very short (1-3 words) and matches common patterns
  const wordCount = normalizedMessage.split(/\s+/).filter(Boolean).length;
  if (wordCount > 3) return false;
  
  return commonResponses.some(item => 
    item.patterns.some(pattern => 
      typeof pattern === 'string' 
        ? normalizedMessage === pattern.toLowerCase()
        : pattern.test(normalizedMessage)
    )
  );
}